import trackUtil from '@/utils/analytics-service';
import { SignInMethod } from '@/utils/analytics/types';

const LAST_AUTH_KEY = 'tg_analytics_last_auth';

/**
 * Map an Auth0 subject to the approved sign_in_method values.
 *
 * Keyed on the connection prefix rather than a substring search: a user who has linked
 * Google to a password account still signs in with whichever connection minted this token.
 */
export function deriveSignInMethod(sub?: string): SignInMethod {
  if (!sub) {
    return 'username_password';
  }
  if (sub.startsWith('google-oauth2|')) {
    return 'google_sso';
  }
  if (sub.startsWith('samlp|') || sub.startsWith('waad|') || sub.startsWith('adfs|')) {
    return 'saml_sso';
  }
  return 'username_password';
}

/**
 * Fire sign_in_completed once per authentication.
 *
 * ACT-011 excludes token refresh, page reload and restored sessions, but the portal
 * learns about a session from /users/me, which resolves on every load. The Auth0 token's
 * `auth_time` is the only value that changes when — and only when — the user actually
 * re-authenticates, so it is the deduplication key. `iat` is the fallback for tenants
 * that do not issue auth_time; it moves on token refresh too, so those tenants will
 * over-count somewhat.
 *
 * The marker is stored per user, so signing in as someone else always counts as new.
 */
export function trackSignInCompletedOnce(params: {
  userId: string;
  organizationId: string;
  sub?: string;
  authTime?: number;
}) {
  const { userId, organizationId, sub, authTime } = params;
  if (!userId || !organizationId || !authTime) {
    return;
  }

  const marker = `${userId}:${authTime}`;
  try {
    if (localStorage.getItem(LAST_AUTH_KEY) === marker) {
      return;
    }
    localStorage.setItem(LAST_AUTH_KEY, marker);
  } catch {
    // Private browsing or blocked storage: fall through and emit. Over-counting is
    // better than a silent gap in the sign-in funnel.
  }

  trackUtil.trackSignInCompleted({
    user_id: userId,
    organization_id: organizationId,
    sign_in_method: deriveSignInMethod(sub),
  });
}

export function resetSignInTracking() {
  try {
    localStorage.removeItem(LAST_AUTH_KEY);
  } catch {
    // nothing to clear
  }
}
